// import external libraries
import { useTranslation } from 'react-i18next';

// import components
import { Tooltip } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// import constants
import { BACKLOG_ROW_ENTITY_TYPES } from '../../Backlog.consts';

// import utils
import { getIconColor, getIconType } from 'components/mrp/mrp.utils';

// import styles
import {
  EntityTypeButton,
  EntityTypeRow
} from './BacklogEntityTypeToggle.styles';

// import types
import type { BacklogEntityTypeToggleProps } from './BacklogEntityTypeToggle.types';

/**
 * Which planning entity's takenlijst is on screen, as the same symbols the grid puts in front of an
 * entity's name. Exactly one is active: a row is one entity, so the types are a single choice rather
 * than a set of filters, and the backend is asked for one type at a time.
 *
 * The active symbol carries the entity's own colour and the rest grey out. Types the backend cannot
 * report on yet are shown disabled — see BACKLOG_ROW_ENTITY_TYPES.
 */
const BacklogEntityTypeToggle = ({
  activeType,
  onChange,
  isVertical
}: BacklogEntityTypeToggleProps) => {
  'use memo';

  const { t } = useTranslation('common');

  return (
    <EntityTypeRow isVertical={isVertical}>
      {BACKLOG_ROW_ENTITY_TYPES.map(({ type, isSupported }) => {
        const label = t(`mrp.entityTypeMap.${type}.single`);
        const isActive = type === activeType;

        return (
          <Tooltip key={type} title={label}>
            <EntityTypeButton
              size="small"
              aria-label={label}
              aria-pressed={isActive}
              disabled={!isSupported}
              isActive={isActive}
              activeColor={getIconColor(type)}
              nudgeRight={type === 'CLIENT'}
              // Exactly one type is always active, so pressing the active one is a no-op rather than a
              // clear — an empty list would be a confusing thing to get from pressing a symbol.
              onClick={() => !isActive && onChange(type)}
            >
              <Icon type={getIconType(type)} />
            </EntityTypeButton>
          </Tooltip>
        );
      })}
    </EntityTypeRow>
  );
};

export default BacklogEntityTypeToggle;
